import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useRef, useEffect, } from "react";
import { KeyBoardKeys } from "../types";
import { Button } from "../button";
import { Divider } from "../divider";
import { useKeyboard } from "@react-aria/interactions";
import { FormField } from "../form";
// components
import { DateInput as TimeInput } from "./date-input";
import { Chips } from "./Chips";
// utils & hooks
import { useOnClickOutside } from "./use-on-click-outside";
import { formatDate, getBeforeMaskedStateChangeHandler, initStringReplacer, isDateValid, } from "./utils";
// styles
import { StyledWrapper, TimePickerWrapper, TimeChipsWrapper, ButtonsWrapper, UppercaseButton, } from "./TimePicker.style";
export function TimePicker({ timeIntervals, format = "24", time, error, nowLabel = "Now", submitButtonLabel = "OK", disabled = false, readonly = false, withSeconds = false, onValueChange, onReset, label, required, }) {
    const [show, setShow] = useState(false);
    const timePickerRef = useRef(null);
    const [val, setVal] = useState("");
    const intervals = Object.assign({ h: 1, m: 1, s: 1 }, timeIntervals);
    const { h: hoursInterval, m: minutesInterval, s: secondsInterval, } = intervals;
    useEffect(() => {
        if (time && isDateValid(time)) {
            setVal(formatDate(time, format, withSeconds));
        }
    }, [time]);
    function syncTimeChips(timeString) {
        var _a;
        const scrollBars = (_a = timePickerRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(".time-chips > div > div");
        if (!timePickerRef.current || scrollBars.length === 0 || !timeString) {
            return;
        }
        const reducedTimeString = timeString.replace(/\s/g, "").replace(/:/g, "");
        const timeChunks = reducedTimeString.match(/.{1,2}/g);
        timeChunks.forEach((chunk, index) => {
            if (index === 2 && !withSeconds) {
                return;
            }
            let chipValue = chunk;
            if (index === 0 && format === "12" && chunk === "12") {
                chipValue = "00";
            }
            const scrollBar = scrollBars[index];
            const buttons = scrollBar === null || scrollBar === void 0 ? void 0 : scrollBar.querySelectorAll(`button[data-chip-value="${chipValue}"]`);
            if (buttons) {
                buttons.forEach((button) => {
                    if (!button.hasAttribute("data-pre-chip-clone-index")) {
                        button.click();
                    }
                });
            }
        });
    }
    function makeButtonsUnFocusable() {
        var _a;
        (_a = timePickerRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll(".time-chips button").forEach((b) => b.setAttribute("tabindex", "-1"));
        // ff has interactive focus-visible also on divs
        timePickerRef.current
            .querySelectorAll(".time-chips div")
            .forEach((b) => b.setAttribute("tabindex", "-1"));
    }
    const hideDatePicker = () => {
        setShow(false);
    };
    const showDatePicker = () => {
        if (readonly || disabled) {
            return;
        }
        setShow(true);
    };
    const onKeyDown = (e) => {
        if (e.key === KeyBoardKeys.Escape)
            hideDatePicker();
    };
    const handleReset = () => {
        setVal("");
        hideDatePicker();
        if (typeof onReset === "function")
            onReset();
    };
    useOnClickOutside(timePickerRef, hideDatePicker);
    const { keyboardProps } = useKeyboard({ onKeyDown });
    useEffect(() => {
        if (show) {
            syncTimeChips(val);
        }
        if (typeof onValueChange === "function") {
            if (!val) {
                onValueChange({
                    time: null,
                    timeString: "",
                });
                return;
            }
            const chunks = val
                .replace(/\s/g, "")
                .replace(/:/g, "")
                .match(/.{1,2}/g);
            const date = new Date();
            const [hh, mm, ss] = chunks;
            if (/^\d+$/.test(hh))
                date.setHours(Number(hh));
            if (/^\d+$/.test(mm))
                date.setMinutes(Number(mm));
            if (/^\d+$/.test(ss))
                date.setSeconds(Number(ss));
            onValueChange({
                time: date,
                timeString: chunks.join(":"),
            });
        }
    }, [show, val]);
    useEffect(makeButtonsUnFocusable, [show]);
    useEffect(() => {
        if (readonly || disabled) {
            hideDatePicker();
        }
    }, [disabled, readonly]);
    const mask = withSeconds ? "99:99:99" : "99:99";
    const maskPlaceholder = withSeconds ? "hh:mm:ss" : "hh:mm";
    const size = withSeconds ? 12 : 7;
    return (_jsxs(TimePickerWrapper, Object.assign({ withSeconds: withSeconds }, keyboardProps, { ref: timePickerRef }, { children: [readonly || disabled ? (_jsx(FormField, { label: label, readOnly: readonly, disabled: disabled, name: "readonlyField", values: {
                    readonlyField: time.toLocaleTimeString().replace(/:/g, " : "),
                } })) : (_jsx(TimeInput, { beforeMaskedStateChange: getBeforeMaskedStateChangeHandler(format, withSeconds), required: required, mask: mask, maskPlaceholder: maskPlaceholder, alwaysShowMask: true, value: val !== "hh:mm:ss" ? val : "", disabled: disabled, readonly: readonly, error: !show ? error : "", size: size, withIcon: true, label: label, onValueChange: (v) => {
                    setVal(v);
                }, withSeconds: withSeconds, onReset: handleReset, onIconClick: showDatePicker, hideDatePicker: hideDatePicker, onFocus: showDatePicker })), show && (_jsxs(StyledWrapper, { children: [_jsxs("div", Object.assign({ className: "focus-area-wrapper" }, { children: [_jsx(Button, { stylingType: "flat", className: "focus-area" }), _jsx(Button, { stylingType: "flat", className: "focus-area" }), withSeconds && (_jsx(Button, { stylingType: "flat", className: "focus-area" }))] })), _jsxs(TimeChipsWrapper, Object.assign({ className: "time-chips" }, { children: [_jsx(Divider, { alignment: "vertical" }), _jsx(Chips, { unit: "h", format: format, interval: hoursInterval, onValueChange: (chipValue) => {
                                    setVal(initStringReplacer(chipValue, maskPlaceholder, format, 0, 2));
                                }, onCloseRequest: hideDatePicker }), _jsx(Divider, { alignment: "vertical" }), _jsx(Chips, { unit: "m", interval: minutesInterval, onValueChange: (chipValue) => {
                                    setVal(initStringReplacer(chipValue, maskPlaceholder, format, 2, 4));
                                }, onCloseRequest: hideDatePicker }), _jsx(Divider, { alignment: "vertical" }), withSeconds && (_jsxs(_Fragment, { children: [_jsx(Chips, { unit: "s", interval: secondsInterval, onValueChange: (chipValue) => {
                                            setVal(initStringReplacer(chipValue, maskPlaceholder, format, 4, 6));
                                        }, onCloseRequest: hideDatePicker }), _jsx(Divider, { alignment: "vertical" })] }))] })), _jsx(Divider, { alignment: "horizontal" }), _jsxs(ButtonsWrapper, { children: [_jsx(Button, Object.assign({ onClick: () => {
                                    setVal(formatDate(new Date(), format, withSeconds));
                                    hideDatePicker();
                                }, stylingType: "flatHighlight" }, { children: nowLabel })), _jsx(UppercaseButton, Object.assign({ onClick: hideDatePicker, stylingType: "flatHighlight" }, { children: submitButtonLabel }))] })] }))] })));
}
